import type { CSSProperties } from 'react';

const HEX_COLOR = /^#[0-9a-fA-F]{3}(?:[0-9a-fA-F]{3})?$/;

/** WCAG-luminance pick of black/white text over the brand color, so a tenant
 *  choosing a pale brand color doesn't get unreadable white-on-pale buttons. */
export function brandForeground(hex: string): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.replace(/./g, (c) => c + c);
  const n = parseInt(h, 16);
  const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((x) => {
    const s = x / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  return luminance > 0.179 ? '#111111' : '#ffffff';
}

/**
 * Inline CSS-variable overrides that re-point the Tailwind theme's `--primary`
 * tokens at the tenant's brand color, so every `bg-primary`/`text-primary`
 * inside the subtree picks it up (index.css maps `--color-primary` to
 * `--primary`). Returns {} for missing/invalid colors so the default theme
 * holds; hex-validated because the value is tenant-authored.
 */
export function brandStyleVars(color: string | null | undefined): CSSProperties {
  if (!color || !HEX_COLOR.test(color)) return {};
  return {
    ['--primary' as never]: color,
    ['--primary-foreground' as never]: brandForeground(color),
  } as CSSProperties;
}
